import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Plus, Trash2, X, Upload, LogOut, Landmark, ExternalLink, Check, Rocket } from "lucide-react";
import { toast } from "sonner";
import { api, money, formatApiError } from "@/lib/api";

const CATEGORIES = ["TEES", "TOPS", "BOTTOMS", "OUTERWEAR", "ACCESSORIES", "SHOES"];
const TABS = ["PRODUCTS", "ORDERS", "PAYOUTS", "LAUNCH", "SETTINGS"];
const EMPTY_FORM = {
  name: "", price: "25.00", category: "TEES", collection: "Classic Dejon Richh",
  sizes: "S,M,L,XL,2XL", description: "", details: "", status: "coming_soon", images: [], drop_date: "",
  members_first: false, stock: null,
};

function toForm(p) {
  return {
    name: p.name, price: String(p.price), category: p.category || "TEES",
    collection: p.collection || "", sizes: (p.sizes || []).join(","),
    description: p.description || "", details: (p.details || []).join("\n"),
    status: p.status || "coming_soon", images: p.images || [],
    drop_date: p.drop_date ? p.drop_date.slice(0, 16) : "",
    members_first: !!p.members_first,
    stock: p.stock ? Object.fromEntries(Object.entries(p.stock).map(([k, v]) => [k, String(v)])) : null,
  };
}

function toPayload(f) {
  return {
    name: f.name.trim(),
    price: parseFloat(f.price),
    category: f.category,
    collection: f.collection.trim(),
    sizes: f.sizes.split(",").map((s) => s.trim()).filter(Boolean),
    description: f.description.trim(),
    details: f.details.split("\n").map((d) => d.trim()).filter(Boolean),
    status: f.status,
    images: f.images,
    drop_date: f.drop_date ? new Date(f.drop_date).toISOString() : null,
    members_first: f.members_first,
    stock: f.stock
      ? Object.fromEntries(Object.entries(f.stock).map(([k, v]) => [k, Math.max(0, parseInt(v, 10) || 0)]))
      : null,
  };
}

const inputCls = "w-full border border-wire bg-transparent px-4 py-3 font-mono text-xs tracking-[0.15em] text-white placeholder:text-neutral-600 focus:border-white focus:outline-none";
const labelCls = "mb-2 block font-mono text-[10px] tracking-[0.3em] text-neutral-500";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("dr_admin_token") || "");
  const [checking, setChecking] = useState(!!token);
  const [authed, setAuthed] = useState(false);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [tab, setTab] = useState("PRODUCTS");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payouts, setPayouts] = useState(null);
  const [launch, setLaunch] = useState(null);
  const [settings, setSettings] = useState(null);
  const [creds, setCreds] = useState({ current_password: "", new_email: "", new_password: "" });
  const [team, setTeam] = useState([]);
  const [teamForm, setTeamForm] = useState({ name: "", email: "", password: "" });
  const [googleError, setGoogleError] = useState("");
  const [authView, setAuthView] = useState("login");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [resetPass, setResetPass] = useState({ a: "", b: "" });
  const location = useLocation();
  const googleProcessed = useRef(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (!location.hash.includes("session_id=") || googleProcessed.current) return;
    googleProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1].split("&")[0];
    (async () => {
      try {
        const { token: t } = await api.adminGoogleSession(sessionId);
        localStorage.setItem("dr_admin_token", t);
        window.history.replaceState(null, "", window.location.pathname);
        setToken(t);
      } catch (err) {
        setGoogleError(formatApiError(err));
        window.history.replaceState(null, "", window.location.pathname);
      }
    })();
  }, [location.hash]);

  useEffect(() => {
    const token = new URLSearchParams(location.search).get("reset_token");
    if (token) {
      setResetToken(token);
      setAuthView("reset");
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [location.search]);

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.adminForgotPassword(forgotEmail);
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (resetPass.a.length < 8) { toast.error("PASSWORD MUST BE AT LEAST 8 CHARACTERS"); return; }
    if (resetPass.a !== resetPass.b) { toast.error("PASSWORDS DON'T MATCH"); return; }
    setBusy(true);
    try {
      await api.adminResetPassword(resetToken, resetPass.a);
      toast.success("PASSWORD UPDATED — LOG IN WITH IT");
      setAuthView("login");
      setResetToken("");
      setResetPass({ a: "", b: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const [form, setForm] = useState(null);
  const [editingSlug, setEditingSlug] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadProducts = () => api.getProducts().then(setProducts).catch(() => {});
  const loadOrders = () => api.adminOrders().then(setOrders).catch((e) => toast.error(formatApiError(e)));
  const loadPayouts = () => api.adminPayouts().then(setPayouts).catch((e) => toast.error(formatApiError(e)));
  const loadLaunch = () => api.adminLaunch().then(setLaunch).catch((e) => toast.error(formatApiError(e)));
  const loadSettings = () => api.adminGetSettings().then(setSettings).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    api.adminVerify()
      .then(() => { setAuthed(true); loadProducts(); loadOrders(); loadPayouts(); loadLaunch(); loadSettings(); loadTeam(); })
      .catch(() => { localStorage.removeItem("dr_admin_token"); setToken(""); })
      .finally(() => setChecking(false));
  }, [token]);

  const saveSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const updated = await api.adminUpdateSettings(settings);
      setSettings(updated);
      toast.success("SETTINGS SAVED");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveCreds = async (e) => {
    e.preventDefault();
    if (!creds.current_password) { toast.error("ENTER YOUR CURRENT PASSWORD"); return; }
    if (!creds.new_email && !creds.new_password) { toast.error("ENTER A NEW EMAIL OR A NEW PASSWORD"); return; }
    setBusy(true);
    try {
      await api.adminUpdateCredentials({
        current_password: creds.current_password,
        new_email: creds.new_email || undefined,
        new_password: creds.new_password || undefined,
      });
      toast.success("CREDENTIALS UPDATED — USE THEM ON YOUR NEXT LOGIN");
      setCreds({ current_password: "", new_email: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const loadTeam = () => api.adminTeam().then(setTeam).catch(() => {});

  const addTeamMember = async (e) => {
    e.preventDefault();
    if (!teamForm.email.includes("@")) { toast.error("ENTER A VALID EMAIL"); return; }
    if (teamForm.password.length < 8) { toast.error("PASSWORD MUST BE AT LEAST 8 CHARACTERS"); return; }
    setBusy(true);
    try {
      await api.adminAddTeamMember({
        email: teamForm.email,
        password: teamForm.password,
        name: teamForm.name || undefined,
      });
      toast.success("ADMIN ADDED — THEY CAN LOG IN AT /admin");
      setTeamForm({ name: "", email: "", password: "" });
      loadTeam();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeTeamMember = async (email) => {
    if (!window.confirm(`Remove admin ${email}?`)) return;
    try {
      await api.adminRemoveTeamMember(email);
      toast.success("ADMIN REMOVED");
      loadTeam();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadSocial = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await api.uploadImage(file);
      setSettings((s) => ({ ...s, social_images: [...(s.social_images || []), url] }));
      toast.success("IMAGE UPLOADED — PRESS SAVE SETTINGS");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { token: t } = await api.adminLogin(login.email, login.password);
      localStorage.setItem("dr_admin_token", t);
      setToken(t);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("dr_admin_token");
    setToken("");
    setAuthed(false);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = toPayload(form);
    if (!payload.name || !(payload.price > 0)) { toast.error("NAME AND VALID PRICE REQUIRED"); return; }
    if (payload.sizes.length === 0) { toast.error("ADD AT LEAST ONE SIZE"); return; }
    setBusy(true);
    try {
      if (editingSlug) {
        await api.updateProduct(editingSlug, payload);
        toast.success("PRODUCT UPDATED");
      } else {
        await api.createProduct(payload);
        toast.success("PRODUCT ADDED");
      }
      setForm(null);
      setEditingSlug(null);
      loadProducts();
      loadLaunch();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete ${slug}? This cannot be undone.`)) return;
    try {
      await api.deleteProduct(slug);
      toast.success("PRODUCT DELETED");
      loadProducts();
      loadLaunch();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await api.uploadImage(file);
      setForm((f) => ({ ...f, images: [...f.images, url] }));
      toast.success("IMAGE UPLOADED");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center pt-16" data-testid="admin-loading">
      <p className="font-mono text-xs tracking-[0.4em] text-neutral-500 animate-pulse">LOADING...</p>
    </div>;
  }

  if (!authed) {
    return (
      <div className="mx-auto max-w-md px-5 pt-40 pb-24" data-testid="admin-login">
        <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">DEJON RICHH — STUDIO</p>

        {authView === "reset" ? (
          <>
            <h1 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-white">Set New Password</h1>
            <form onSubmit={submitReset} className="mt-10 space-y-4" data-testid="reset-form">
              <div>
                <label className={labelCls}>NEW PASSWORD (MIN 8 CHARACTERS)</label>
                <input data-testid="reset-password-input" type="password" value={resetPass.a}
                  onChange={(e) => setResetPass({ ...resetPass, a: e.target.value })} className={inputCls} placeholder="NEW PASSWORD" autoComplete="new-password" />
              </div>
              <div>
                <label className={labelCls}>CONFIRM NEW PASSWORD</label>
                <input data-testid="reset-confirm-input" type="password" value={resetPass.b}
                  onChange={(e) => setResetPass({ ...resetPass, b: e.target.value })} className={inputCls} placeholder="CONFIRM PASSWORD" autoComplete="new-password" />
              </div>
              <button data-testid="reset-submit-button" disabled={busy}
                className="flex w-full items-center justify-center gap-3 bg-white py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300 disabled:opacity-60">
                {busy ? "UPDATING..." : "SET NEW PASSWORD"}
              </button>
            </form>
          </>
        ) : authView === "forgot" ? (
          <>
            <h1 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-white">Reset Password</h1>
            {forgotSent ? (
              <div className="mt-10 border border-wire px-6 py-8" data-testid="forgot-sent">
                <p className="font-mono text-xs leading-relaxed tracking-[0.2em] text-neutral-300">
                  IF THAT'S THE ADMIN EMAIL, A RESET LINK IS ON ITS WAY. IT EXPIRES IN 1 HOUR.
                </p>
                <button data-testid="forgot-back-to-login" onClick={() => { setAuthView("login"); setForgotSent(false); }}
                  className="mt-6 font-mono text-[10px] tracking-[0.3em] text-neutral-400 underline underline-offset-4 hover:text-white">
                  BACK TO LOGIN
                </button>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="mt-10 space-y-4" data-testid="forgot-form">
                <div>
                  <label className={labelCls}>ADMIN EMAIL</label>
                  <input data-testid="forgot-email-input" type="email" value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)} className={inputCls} placeholder="YOUR ADMIN EMAIL" />
                </div>
                <button data-testid="forgot-submit-button" disabled={busy}
                  className="flex w-full items-center justify-center gap-3 bg-white py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300 disabled:opacity-60">
                  {busy ? "SENDING..." : "EMAIL ME A RESET LINK"}
                </button>
                <button type="button" data-testid="forgot-cancel" onClick={() => setAuthView("login")}
                  className="w-full text-center font-mono text-[10px] tracking-[0.3em] text-neutral-500 underline underline-offset-4 hover:text-white">
                  BACK TO LOGIN
                </button>
              </form>
            )}
          </>
        ) : (
          <>
            <h1 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-white">Admin Login</h1>
            <form onSubmit={doLogin} className="mt-10 space-y-4">
              <div>
                <label className={labelCls}>EMAIL</label>
                <input data-testid="admin-email-input" type="email" value={login.email}
                  onChange={(e) => setLogin({ ...login, email: e.target.value })} className={inputCls} placeholder="ADMIN EMAIL" />
              </div>
              <div>
                <label className={labelCls}>PASSWORD</label>
                <input data-testid="admin-password-input" type="password" value={login.password}
                  onChange={(e) => setLogin({ ...login, password: e.target.value })} className={inputCls} placeholder="PASSWORD" />
              </div>
              <button data-testid="admin-login-button" disabled={busy}
                className="flex w-full items-center justify-center gap-3 bg-white py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300 disabled:opacity-60">
                {busy ? "SIGNING IN..." : "SIGN IN"}
              </button>
              <button type="button" data-testid="forgot-password-link" onClick={() => setAuthView("forgot")}
                className="w-full text-center font-mono text-[10px] tracking-[0.3em] text-neutral-500 underline underline-offset-4 hover:text-white">
                FORGOT PASSWORD?
              </button>
            </form>

            <div className="mt-6 flex items-center gap-4">
              <span className="h-px flex-1 bg-wire" />
              <span className="font-mono text-[9px] tracking-[0.3em] text-neutral-600">OR</span>
              <span className="h-px flex-1 bg-wire" />
            </div>

            <button data-testid="google-login-button" onClick={googleLogin}
              className="mt-6 flex w-full items-center justify-center gap-3 border border-wire py-4 font-mono text-[11px] tracking-[0.35em] text-neutral-200 transition-colors hover:border-white hover:text-white">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              SIGN IN WITH GOOGLE
            </button>
            {googleError && (
              <p className="mt-4 border border-wire px-4 py-3 font-mono text-[10px] leading-relaxed tracking-[0.15em] text-neutral-400" data-testid="google-login-error">
                {googleError}
              </p>
            )}
            <p className="mt-6 font-mono text-[9px] leading-relaxed tracking-[0.2em] text-neutral-600">
              GOOGLE SIGN-IN ONLY WORKS FOR THE GOOGLE ACCOUNT THAT MATCHES YOUR ADMIN EMAIL (SET IT IN SETTINGS → ADMIN CREDENTIALS).
            </p>
          </>
        )}
      </div>
    );
  }

  const launchSteps = launch ? [
    {
      title: "Claim your Stripe account",
      note: "One click. This is where your money lives and where payouts come from.",
      done: launch.stripe.details_submitted,
      action: { label: "CLAIM ACCOUNT", href: launch.onboarding_url },
    },
    {
      title: "Verify your identity (KYC)",
      note: "Stripe asks for basic business + ID details. Required for any real store.",
      done: launch.stripe.charges_enabled,
      action: { label: "CONTINUE IN STRIPE", href: launch.onboarding_url },
    },
    {
      title: "Add your bank for direct deposit",
      note: "Routing + account number inside Stripe. Payouts then arrive automatically every 2 days.",
      done: launch.stripe.payouts_enabled,
      action: { label: "ADD BANK IN STRIPE", href: launch.onboarding_url },
    },
    {
      title: "Add your products",
      note: "Products tab → ADD PRODUCT. Photos, price, sizes, drop date.",
      done: launch.products_total >= 1,
      action: { label: "ADD PRODUCT", tab: "PRODUCTS" },
    },
    {
      title: "Publish at least one live product",
      note: "Flip a product from COMING SOON to AVAILABLE NOW when you're ready to sell it.",
      done: launch.products_live >= 1,
      action: { label: "MANAGE PRODUCTS", tab: "PRODUCTS" },
    },
    {
      title: "Press Deploy",
      note: "The Deploy button (top right) publishes the store. Stripe switches to live keys automatically once your account is claimed — expect one automatic redeploy when that happens.",
      done: launch.mode === "live",
    },
  ] : [];

  const launchDone = launchSteps.filter((s) => s.done).length;

  return (
    <div className="mx-auto max-w-[1200px] px-5 sm:px-8 pt-32 pb-24" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">DEJON RICHH — STUDIO</p>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white">Dashboard</h1>
        </div>
        <button data-testid="admin-logout-button" onClick={logout}
          className="flex items-center gap-2 border border-wire px-5 py-3 font-mono text-[10px] tracking-[0.3em] text-neutral-400 hover:border-white hover:text-white">
          <LogOut size={13} /> LOGOUT
        </button>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 border-b border-wire" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t} data-testid={`admin-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`px-6 py-3 font-mono text-[10px] tracking-[0.3em] transition-colors ${
              tab === t ? "border-b border-white text-white" : "text-neutral-500 hover:text-white"
            }`}>
            {t}
          </button>
        ))}
      </div>

      {/* ---------------- PRODUCTS TAB ---------------- */}
      {tab === "PRODUCTS" && (
        <div>
          <div className="mt-8">
            <button data-testid="admin-add-product-button"
              onClick={() => { setForm({ ...EMPTY_FORM }); setEditingSlug(null); }}
              className="flex items-center gap-2 bg-white px-6 py-3 font-mono text-[10px] tracking-[0.3em] text-black transition-colors hover:bg-neutral-300">
              <Plus size={13} /> ADD PRODUCT
            </button>
          </div>

          {form && (
            <form onSubmit={save} className="mt-8 border border-wire bg-charcoal/40 p-6 sm:p-10" data-testid="product-form">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-400">
                  {editingSlug ? `EDIT — ${editingSlug.toUpperCase()}` : "NEW PRODUCT"}
                </p>
                <button type="button" data-testid="product-form-close" onClick={() => { setForm(null); setEditingSlug(null); }}
                  className="text-neutral-500 hover:text-white" aria-label="Close form"><X size={16} /></button>
              </div>

              <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>PRODUCT NAME *</label>
                  <input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls} placeholder="E.G. DR1 GRAFFITI TEE" />
                </div>
                <div>
                  <label className={labelCls}>PRICE (USD) *</label>
                  <input data-testid="product-price-input" type="number" step="0.01" min="0" value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} placeholder="25.00" />
                </div>
                <div>
                  <label className={labelCls}>CATEGORY</label>
                  <select data-testid="product-category-select" value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-wire bg-obsidian px-4 py-3 font-mono text-xs tracking-[0.15em] text-white focus:border-white focus:outline-none">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>COLLECTION</label>
                  <input data-testid="product-collection-input" value={form.collection}
                    onChange={(e) => setForm({ ...form, collection: e.target.value })} className={inputCls} placeholder="E.G. DR1 GRAFFITI" />
                </div>
                <div>
                  <label className={labelCls}>SIZES (COMMA SEPARATED)</label>
                  <input data-testid="product-sizes-input" value={form.sizes}
                    onChange={(e) => setForm({ ...form, sizes: e.target.value })} className={inputCls} placeholder="S,M,L,XL,2XL" />
                </div>
                <div>
                  <label className={labelCls}>STATUS</label>
                  <div className="grid grid-cols-3 gap-2" data-testid="product-status-toggle">
                    <button type="button" data-testid="status-coming-soon"
                      onClick={() => setForm({ ...form, status: "coming_soon" })}
                      className={`border py-3 font-mono text-[9px] tracking-[0.2em] transition-colors ${form.status === "coming_soon" ? "border-white bg-white text-black" : "border-wire text-neutral-400 hover:border-neutral-500"}`}>
                      COMING SOON
                    </button>
                    <button type="button" data-testid="status-live"
                      onClick={() => setForm({ ...form, status: "live" })}
                      className={`border py-3 font-mono text-[9px] tracking-[0.2em] transition-colors ${form.status === "live" ? "border-white bg-white text-black" : "border-wire text-neutral-400 hover:border-neutral-500"}`}>
                      AVAILABLE NOW
                    </button>
                    <button type="button" data-testid="status-sold-out"
                      onClick={() => setForm({ ...form, status: "sold_out" })}
                      className={`border py-3 font-mono text-[9px] tracking-[0.2em] transition-colors ${form.status === "sold_out" ? "border-white bg-white text-black" : "border-wire text-neutral-400 hover:border-neutral-500"}`}>
                      SOLD OUT
                    </button>
                  </div>
                </div>
                {form.status === "coming_soon" && (
                  <div className="sm:col-span-2">
                    <label className={labelCls}>DROP DATE & TIME (OPTIONAL — SHOWS A LIVE COUNTDOWN ON THE PRODUCT PAGE)</label>
                    <input data-testid="product-drop-date-input" type="datetime-local" value={form.drop_date}
                      onChange={(e) => setForm({ ...form, drop_date: e.target.value })}
                      className={`${inputCls} [color-scheme:dark]`} />
                  </div>
                )}
                <div className="sm:col-span-2">
                  <button type="button" data-testid="members-first-toggle"
                    onClick={() => setForm({ ...form, members_first: !form.members_first })}
                    className={`w-full border py-3.5 font-mono text-[10px] tracking-[0.25em] transition-colors ${form.members_first ? "border-white bg-white text-black" : "border-wire text-neutral-400 hover:border-neutral-500"}`}>
                    MEMBERS ONLY — FIRST 24 HOURS: {form.members_first ? "ON" : "OFF"}
                  </button>
                  <p className="mt-2 font-mono text-[9px] tracking-[0.2em] text-neutral-600">
                    WHEN ON, ONLY SIGNED-IN MEMBERS CAN BUY FOR THE FIRST 24 HOURS AFTER THE DROP GOES LIVE.
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <button type="button" data-testid="stock-toggle"
                    onClick={() => {
                      if (form.stock) {
                        setForm({ ...form, stock: null });
                      } else {
                        const sizes = form.sizes.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
                        setForm({ ...form, stock: Object.fromEntries(sizes.map((s) => [s, "5"])) });
                      }
                    }}
                    className={`w-full border py-3.5 font-mono text-[10px] tracking-[0.25em] transition-colors ${form.stock ? "border-white bg-white text-black" : "border-wire text-neutral-400 hover:border-neutral-500"}`}>
                    LIMITED RUN STOCK: {form.stock ? "ON" : "OFF"}
                  </button>
                  <p className="mt-2 font-mono text-[9px] tracking-[0.2em] text-neutral-600">
                    WHEN ON, SET UNITS PER SIZE. PAID ORDERS BURN STOCK AND THE DROP MARKS ITSELF SOLD OUT AT ZERO.
                  </p>
                  {form.stock && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="stock-inputs">
                      {form.sizes.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean).map((s) => (
                        <div key={s} className="flex items-center gap-2 border border-wire px-3 py-2">
                          <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-400">{s}</span>
                          <input data-testid={`stock-input-${s.toLowerCase()}`} type="number" min="0" value={form.stock[s] ?? "0"}
                            onChange={(e) => setForm({ ...form, stock: { ...form.stock, [s]: e.target.value } })}
                            className="w-16 border border-wire bg-transparent px-2 py-1 font-mono text-xs text-white focus:border-white focus:outline-none" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <label className={labelCls}>DESCRIPTION</label>
                <textarea data-testid="product-description-input" rows={3} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} placeholder="PRODUCT DESCRIPTION" />
              </div>
              <div className="mt-5">
                <label className={labelCls}>DETAILS (ONE PER LINE)</label>
                <textarea data-testid="product-details-input" rows={3} value={form.details}
                  onChange={(e) => setForm({ ...form, details: e.target.value })} className={`${inputCls} resize-none`}
                  placeholder={"Premium cotton\nRelaxed fit\nUnisex"} />
              </div>

              <div className="mt-5">
                <label className={labelCls}>IMAGES (FIRST = FRONT, THEN BACK, DETAIL...)</label>
                <div className="flex flex-wrap items-center gap-3">
                  {form.images.map((src, i) => (
                    <div key={src + i} className="relative h-24 w-24 border border-wire bg-obsidian" data-testid={`product-image-${i}`}>
                      <img src={src} alt="" className="h-full w-full object-cover" />
                      <button type="button" data-testid={`product-image-remove-${i}`}
                        onClick={() => setForm({ ...form, images: form.images.filter((_, x) => x !== i) })}
                        className="absolute -right-2 -top-2 border border-wire bg-obsidian p-1 text-neutral-400 hover:text-white" aria-label="Remove image">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                  <label data-testid="product-image-upload"
                    className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-wire text-neutral-500 transition-colors hover:border-white hover:text-white">
                    <Upload size={16} />
                    <span className="font-mono text-[8px] tracking-[0.2em]">{uploading ? "UPLOADING" : "UPLOAD"}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} />
                  </label>
                </div>
                <input data-testid="product-image-url-input" className={`${inputCls} mt-3`} placeholder="OR PASTE IMAGE URL AND PRESS ENTER"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const v = e.target.value.trim();
                      if (v) setForm({ ...form, images: [...form.images, v] });
                      e.target.value = "";
                    }
                  }} />
              </div>

              <button data-testid="product-save-button" disabled={busy || uploading}
                className="mt-8 flex items-center gap-3 bg-white px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300 disabled:opacity-60">
                {busy ? "SAVING..." : <>{editingSlug ? "SAVE CHANGES" : "ADD PRODUCT"} <ArrowRight size={13} /></>}
              </button>
            </form>
          )}

          <div className="mt-8 border-t border-wire" data-testid="admin-product-list">
            {products.length === 0 && (
              <p className="py-16 text-center font-mono text-xs tracking-[0.3em] text-neutral-600">NO PRODUCTS YET</p>
            )}
            {products.map((p) => (
              <div key={p.slug} data-testid={`admin-product-${p.slug}`} className="flex flex-wrap items-center gap-4 border-b border-wire py-5">
                <div className="h-16 w-14 shrink-0 overflow-hidden border border-wire bg-charcoal">
                  {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-[200px] flex-1">
                  <p className="font-display text-sm font-bold uppercase tracking-wide text-white">{p.name}</p>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-neutral-500">
                    {p.category} — {(p.sizes || []).join(" / ")}
                    {p.status !== "live" && p.drop_date && ` — DROPS ${new Date(p.drop_date).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).toUpperCase()}`}
                    {p.stock && ` — STOCK ${Object.values(p.stock).reduce((a, b) => a + b, 0)}${p.run_size ? `/${p.run_size}` : ""}`}
                  </p>
                </div>
                <p className="font-mono text-sm text-neutral-200">{money(p.price)}</p>
                <span data-testid={`admin-status-${p.slug}`}
                  className={`border px-3 py-1.5 font-mono text-[9px] tracking-[0.25em] ${p.status === "live" ? "border-white text-white" : "border-wire text-neutral-500"}`}>
                  {p.status === "live" ? "LIVE" : p.status === "sold_out" ? "SOLD OUT" : "COMING SOON"}
                </span>
                <div className="flex gap-2">
                  <button data-testid={`admin-edit-${p.slug}`}
                    onClick={() => { setForm(toForm(p)); setEditingSlug(p.slug); window.__lenis?.scrollTo(0, { immediate: true }); }}
                    className="border border-wire px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-neutral-300 hover:border-white hover:text-white">
                    EDIT
                  </button>
                  <button data-testid={`admin-delete-${p.slug}`} onClick={() => remove(p.slug)}
                    className="border border-wire p-2 text-neutral-500 hover:border-white hover:text-white" aria-label="Delete product">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- ORDERS TAB ---------------- */}
      {tab === "ORDERS" && (
        <div className="mt-8 border-t border-wire" data-testid="admin-orders-list">
          {orders.length === 0 && (
            <p className="py-16 text-center font-mono text-xs tracking-[0.3em] text-neutral-600">NO ORDERS YET</p>
          )}
          {orders.map((o) => (
            <div key={o.order_number} data-testid={`admin-order-${o.order_number}`} className="flex flex-wrap items-center gap-4 border-b border-wire py-5">
              <p className="font-mono text-sm tracking-[0.15em] text-white">{o.order_number}</p>
              <div className="min-w-[220px] flex-1">
                <p className="font-mono text-[11px] text-neutral-300">
                  {(o.items || []).map((i) => `${i.name} (${i.size}) x${i.qty}`).join(" · ")}
                </p>
                <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-neutral-500">
                  {o.email || "—"} — {(o.created_at || "").slice(0, 10)}
                </p>
              </div>
              <p className="font-mono text-sm text-neutral-200">{o.total ? money(o.total) : money(o.subtotal)}</p>
              <span className={`border px-3 py-1.5 font-mono text-[9px] tracking-[0.25em] ${o.status === "paid" ? "border-white text-white" : "border-wire text-neutral-500"}`}>
                {o.status === "paid" ? "PAID" : "AWAITING PAYMENT"}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- PAYOUTS TAB ---------------- */}
      {tab === "PAYOUTS" && payouts && (
        <div className="mt-8" data-testid="admin-payouts">
          <div className="grid grid-cols-1 gap-px border border-wire bg-wire sm:grid-cols-3">
            <div className="bg-obsidian p-8">
              <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">TOTAL SALES</p>
              <p className="mt-3 font-display text-3xl font-extrabold text-white" data-testid="payouts-revenue">{money(payouts.revenue)}</p>
              <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-neutral-600">{payouts.paid_orders} PAID ORDER(S)</p>
            </div>
            <div className="bg-obsidian p-8">
              <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">STRIPE BALANCE — AVAILABLE</p>
              <p className="mt-3 font-display text-3xl font-extrabold text-white" data-testid="payouts-available">{money(payouts.balance.available)}</p>
              <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-neutral-600">READY TO PAY OUT</p>
            </div>
            <div className="bg-obsidian p-8">
              <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">STRIPE BALANCE — PENDING</p>
              <p className="mt-3 font-display text-3xl font-extrabold text-white" data-testid="payouts-pending">{money(payouts.balance.pending)}</p>
              <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-neutral-600">CLEARING (USUALLY 2 BUSINESS DAYS)</p>
            </div>
          </div>

          <div className="mt-px border border-t-0 border-wire bg-charcoal/40 p-8 sm:p-10">
            <div className="flex items-center gap-3">
              <Landmark size={18} className="text-neutral-300" />
              <p className="font-display text-lg font-bold uppercase tracking-wide text-white">Direct Deposit Setup</p>
            </div>
            <p className="mt-5 max-w-2xl font-mono text-xs leading-relaxed tracking-wider text-neutral-400" data-testid="payouts-explainer">
              Your sales money is held securely in your Stripe balance and paid out to your bank account by direct deposit — automatically, on a rolling 2-day schedule once set up. For your security, bank details are only ever entered inside Stripe (never on this site).
            </p>
            <ol className="mt-6 max-w-2xl space-y-3 font-mono text-[11px] leading-relaxed tracking-wider text-neutral-400">
              <li><span className="text-neutral-600">01 /</span> Claim your Stripe account with the button below (one click, free)</li>
              <li><span className="text-neutral-600">02 /</span> Complete identity verification (required for any real business)</li>
              <li><span className="text-neutral-600">03 /</span> Add your bank account + routing number in Stripe — payouts then arrive by direct deposit automatically</li>
            </ol>
            <a href={payouts.onboarding_url} target="_blank" rel="noopener noreferrer" data-testid="payouts-claim-button"
              className="mt-8 inline-flex items-center gap-3 bg-white px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300">
              SET UP PAYOUTS <ExternalLink size={13} />
            </a>
            <p className="mt-4 font-mono text-[9px] tracking-[0.25em] text-neutral-600">
              {payouts.mode === "test" ? "CURRENTLY IN TEST MODE — BALANCES BECOME REAL AFTER YOU CLAIM AND GO LIVE" : "LIVE MODE"}
            </p>
          </div>
        </div>
      )}

      {/* ---------------- LAUNCH TAB ---------------- */}
      {tab === "LAUNCH" && launch && (
        <div className="mt-8" data-testid="admin-launch">
          <div className="flex items-center gap-4 border border-wire bg-charcoal/40 p-8">
            <Rocket size={20} className="text-neutral-300" />
            <div className="flex-1">
              <p className="font-display text-lg font-bold uppercase tracking-wide text-white">Launch Checklist</p>
              <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-neutral-500">{launchDone} OF {launchSteps.length} COMPLETE</p>
            </div>
            <div className="hidden sm:block h-1 w-40 bg-wire">
              <div className="h-1 bg-white transition-all" style={{ width: `${(launchDone / launchSteps.length) * 100}%` }} data-testid="launch-progress" />
            </div>
          </div>
          <div className="mt-px border border-t-0 border-wire">
            {launchSteps.map((step, i) => (
              <div key={step.title} data-testid={`launch-step-${i}`} className="flex flex-wrap items-center gap-4 border-b border-wire px-6 py-6 last:border-b-0">
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center border ${step.done ? "border-white bg-white text-black" : "border-wire text-transparent"}`}>
                  <Check size={13} />
                </div>
                <div className="min-w-[220px] flex-1">
                  <p className={`font-mono text-xs tracking-[0.2em] ${step.done ? "text-neutral-500 line-through" : "text-white"}`}>{step.title.toUpperCase()}</p>
                  <p className="mt-1 font-mono text-[10px] leading-relaxed tracking-wider text-neutral-500">{step.note}</p>
                </div>
                {step.done ? (
                  <span className="font-mono text-[9px] tracking-[0.3em] text-neutral-500">DONE</span>
                ) : step.action?.href ? (
                  <a href={step.action.href} target="_blank" rel="noopener noreferrer" data-testid={`launch-action-${i}`}
                    className="flex items-center gap-2 bg-white px-5 py-2.5 font-mono text-[9px] tracking-[0.3em] text-black transition-colors hover:bg-neutral-300">
                    {step.action.label} <ExternalLink size={11} />
                  </a>
                ) : step.action?.tab ? (
                  <button data-testid={`launch-action-${i}`} onClick={() => setTab(step.action.tab)}
                    className="border border-wire px-5 py-2.5 font-mono text-[9px] tracking-[0.3em] text-neutral-300 transition-colors hover:border-white hover:text-white">
                    {step.action.label}
                  </button>
                ) : (
                  <span className="font-mono text-[9px] tracking-[0.3em] text-neutral-500">TOP-RIGHT BUTTON</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- SETTINGS TAB ---------------- */}
      {tab === "SETTINGS" && settings && (
        <form onSubmit={saveSettings} className="mt-8 max-w-2xl border border-wire bg-charcoal/40 p-8 sm:p-10" data-testid="settings-form">
          <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-400">STORE SETTINGS</p>

          <div className="mt-8">
            <label className={labelCls}>NOTIFICATION EMAIL — SALE ALERTS & CONTACT MESSAGES GO HERE</label>
            <input data-testid="settings-owner-email-input" type="email" value={settings.owner_email || ""}
              onChange={(e) => setSettings({ ...settings, owner_email: e.target.value })}
              className={inputCls} placeholder="YOUR REAL EMAIL ADDRESS" />
            <p className="mt-2 font-mono text-[9px] tracking-[0.2em] text-neutral-600">
              BUYER ORDER CONFIRMATIONS ALWAYS GO TO THE BUYER AUTOMATICALLY.
            </p>
          </div>

          <div className="mt-8">
            <label className={labelCls}>INSTAGRAM HANDLE (WITHOUT @)</label>
            <input data-testid="settings-ig-handle-input" value={settings.ig_handle || ""}
              onChange={(e) => setSettings({ ...settings, ig_handle: e.target.value })}
              className={inputCls} placeholder="dejonrichh" />
          </div>

          <div className="mt-8">
            <label className={labelCls}>HOMEPAGE SOCIAL STRIP IMAGES (UP TO 12 — FALLS BACK TO PRODUCT ART)</label>
            <div className="flex flex-wrap items-center gap-3">
              {(settings.social_images || []).map((src, i) => (
                <div key={src + i} className="relative h-20 w-20 border border-wire bg-obsidian" data-testid={`social-image-${i}`}>
                  <img src={src} alt="" className="h-full w-full object-cover" />
                  <button type="button" data-testid={`social-image-remove-${i}`}
                    onClick={() => setSettings({ ...settings, social_images: settings.social_images.filter((_, x) => x !== i) })}
                    className="absolute -right-2 -top-2 border border-wire bg-obsidian p-1 text-neutral-400 hover:text-white" aria-label="Remove image">
                    <X size={10} />
                  </button>
                </div>
              ))}
              <label data-testid="social-image-upload"
                className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 border border-dashed border-wire text-neutral-500 transition-colors hover:border-white hover:text-white">
                <Upload size={14} />
                <span className="font-mono text-[8px] tracking-[0.2em]">{uploading ? "..." : "UPLOAD"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={uploadSocial} disabled={uploading} />
              </label>
            </div>
          </div>

          <button data-testid="settings-save-button" disabled={busy || uploading}
            className="mt-10 flex items-center gap-3 bg-white px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300 disabled:opacity-60">
            {busy ? "SAVING..." : "SAVE SETTINGS"}
          </button>
        </form>
      )}

      {tab === "SETTINGS" && settings && (
        <form onSubmit={saveCreds} className="mt-6 max-w-2xl border border-wire bg-charcoal/40 p-8 sm:p-10" data-testid="credentials-form">
          <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-400">ADMIN CREDENTIALS — WHO CAN LOG IN HERE</p>
          <div className="mt-8 grid grid-cols-1 gap-5">
            <div>
              <label className={labelCls}>CURRENT PASSWORD *</label>
              <input data-testid="creds-current-password" type="password" value={creds.current_password}
                onChange={(e) => setCreds({ ...creds, current_password: e.target.value })}
                className={inputCls} placeholder="CURRENT PASSWORD" autoComplete="current-password" />
            </div>
            <div>
              <label className={labelCls}>NEW ADMIN EMAIL (OPTIONAL)</label>
              <input data-testid="creds-new-email" type="email" value={creds.new_email}
                onChange={(e) => setCreds({ ...creds, new_email: e.target.value })}
                className={inputCls} placeholder="LEAVE EMPTY TO KEEP CURRENT" autoComplete="off" />
            </div>
            <div>
              <label className={labelCls}>NEW PASSWORD (OPTIONAL, MIN 8 CHARACTERS)</label>
              <input data-testid="creds-new-password" type="password" value={creds.new_password}
                onChange={(e) => setCreds({ ...creds, new_password: e.target.value })}
                className={inputCls} placeholder="LEAVE EMPTY TO KEEP CURRENT" autoComplete="new-password" />
            </div>
          </div>
          <button data-testid="creds-save-button" disabled={busy}
            className="mt-8 flex items-center gap-3 border border-white px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-white transition-colors hover:bg-white hover:text-black disabled:opacity-60">
            {busy ? "UPDATING..." : "UPDATE CREDENTIALS"}
          </button>
          <p className="mt-4 font-mono text-[9px] leading-relaxed tracking-[0.2em] text-neutral-600">
            TAKES EFFECT ON YOUR NEXT LOGIN — YOUR CURRENT SESSION STAYS ACTIVE. WRITE THE NEW PASSWORD DOWN; THERE'S NO RECOVERY EMAIL YET.
          </p>
        </form>
      )}

      {tab === "SETTINGS" && settings && (
        <div className="mt-6 max-w-2xl border border-wire bg-charcoal/40 p-8 sm:p-10" data-testid="team-section">
          <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-400">TEAM — WHO ELSE CAN RUN THE STORE</p>
          <p className="mt-3 font-mono text-[10px] leading-relaxed tracking-[0.2em] text-neutral-600">
            ADMINS YOU ADD CAN LOG IN AT /ADMIN WITH THEIR EMAIL + PASSWORD, OR WITH GOOGLE IF THEIR GOOGLE ACCOUNT MATCHES. THERE IS NO PUBLIC ADMIN SIGN-UP — THAT WOULD LET ANYONE TAKE OVER THE STORE.
          </p>

          <div className="mt-6 divide-y divide-wire border-y border-wire" data-testid="team-list">
            {team.map((a) => (
              <div key={a.email} className="flex items-center gap-4 py-4" data-testid={`team-member-${a.email.replace(/[^a-z0-9]/g, "-")}`}>
                <div className="flex-1">
                  <p className="font-mono text-xs text-neutral-200">{a.email}</p>
                  {a.name && <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-neutral-500">{a.name.toUpperCase()}</p>}
                </div>
                {team.length > 1 && (
                  <button data-testid={`team-remove-${a.email.replace(/[^a-z0-9]/g, "-")}`} onClick={() => removeTeamMember(a.email)}
                    className="border border-wire p-2 text-neutral-500 hover:border-white hover:text-white" aria-label={`Remove ${a.email}`}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={addTeamMember} className="mt-8 space-y-4" data-testid="team-add-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>NAME (OPTIONAL)</label>
                <input data-testid="team-name-input" value={teamForm.name}
                  onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })} className={inputCls} placeholder="E.G. STORE MANAGER" />
              </div>
              <div>
                <label className={labelCls}>EMAIL *</label>
                <input data-testid="team-email-input" type="email" value={teamForm.email}
                  onChange={(e) => setTeamForm({ ...teamForm, email: e.target.value })} className={inputCls} placeholder="THEIR EMAIL" />
              </div>
            </div>
            <div>
              <label className={labelCls}>TEMPORARY PASSWORD * (MIN 8 CHARACTERS — THEY CAN CHANGE IT AFTER)</label>
              <input data-testid="team-password-input" type="password" value={teamForm.password}
                onChange={(e) => setTeamForm({ ...teamForm, password: e.target.value })} className={inputCls} placeholder="TEMPORARY PASSWORD" autoComplete="new-password" />
            </div>
            <button data-testid="team-add-button" disabled={busy}
              className="flex items-center gap-3 border border-white px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-white transition-colors hover:bg-white hover:text-black disabled:opacity-60">
              <Plus size={13} /> ADD ADMIN
            </button>
          </form>
        </div>
      )}

      <p className="mt-10 font-mono text-[10px] tracking-[0.25em] text-neutral-600">
        STOREFRONT: <Link to="/shop" className="text-neutral-400 underline underline-offset-4 hover:text-white">VIEW SHOP</Link>
      </p>
    </div>
  );
}
